/**
 * OutcomeHours: the school modal's "Best hours" block. Positive outcomes by hour of day (India time):
 * a best-hours line, 24 hour columns, then a weekday × hour heatmap.
 *
 * Port of LAE `features/productivity/charts/OutcomeHours.jsx` + `charts/WeekdayHourHeatmap.jsx`
 * (spec §7.3). The pure maths lives in `lae-hour-pattern.ts`.
 *
 * Deliberate differences from LAE:
 * - The section title ("Best hours for positive outcomes") is rendered by the modal's section wrapper,
 *   not here, matching LAE where the heading sat outside this component.
 * - Every hover help (the best-hours line, the legend, `BEST_HOURS_HINT`) is dropped: no helper text.
 *   The chart readout and the heatmap cell readout stay; they are data, not help.
 * - Heatmap ramp: the LAE raw emerald scale becomes `var(--success)` mixed into `var(--surface-sunken)`
 *   at five steps, so it is token-only and follows dark mode.
 * - The cell readout is portalled on the popover rung (60) and placed by a small local clamp,
 *   since EMERGE has no `placeFloating`. Cells keep the global token focus outline.
 * - The controlled legend props are dropped: one series has no legend.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/lib/cx';
import type { LaeProdOutcomeHours } from '@/lib/types';
import { ColumnChart } from './ColumnChart';
import type { ColumnChartSeries, ColumnChartTooltipRow } from './ColumnChart';
import { DIRECTION_FILL, metricLabel } from './lae-metrics';
import { fmtNum } from './lae-format';
import { HOURS_IN_DAY, bestOutcomeWindow, heatStep, hourLabel, hourSpan, outcomeRows } from './lae-hour-pattern';

const HOUR_BUCKETS = Array.from({ length: HOURS_IN_DAY }, (_, h) => String(h));
const X_TICKS = [0, 4, 8, 12, 16, 20] as const;
const pad2 = (h: number) => String(h).padStart(2, '0');
const axisLabel = (bucket: string) => pad2(Number(bucket));
const tooltipLabel = (bucket: string) => hourLabel(Number(bucket));
const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export interface OutcomeHoursProps {
  /** `outcome_hours` of the school endpoint; null / undefined renders nothing. */
  data: LaeProdOutcomeHours | null | undefined;
  className?: string;
}

export function OutcomeHours({ data, className }: OutcomeHoursProps) {
  const rows = useMemo(() => outcomeRows(data?.hours), [data]);
  if (!data || typeof data !== 'object') return null;

  const total = num(data.positive_total) || rows.reduce((a, r) => a + r.positive, 0);
  if (!(total > 0)) {
    return <p className={cx('text-sm text-fg-muted', className)}>No positive outcomes in this period</p>;
  }

  const win = bestOutcomeWindow(rows, total);
  const series: ColumnChartSeries[] = [
    { key: 'positive', label: 'Positive outcomes', values: rows.map((r) => r.positive), fill: DIRECTION_FILL.positive },
  ];
  const tooltipRows = (i: number): ColumnChartTooltipRow[] => {
    const r = rows[i];
    if (!r) return [];
    return [
      { label: 'Calls Answered', value: fmtNum(r.connected) },
      { label: 'Outcome rate', value: r.connected ? `${Math.round((r.positive / r.connected) * 100)}%` : '—' },
      { label: metricLabel('counselling_completed'), value: fmtNum(r.cc) },
    ];
  };

  return (
    <div className={cx('min-w-0', className)}>
      <p className="text-sm text-fg-muted">
        {win && (
          <>
            Best hours <span className="tabular-nums text-fg">{hourSpan(win.start, win.end)}</span>
            <span className="px-1 text-fg-subtle">·</span>
            <span className="tabular-nums text-fg">{win.share}%</span> of positive outcomes
            <span className="px-1 text-fg-subtle">·</span>
          </>
        )}
        India time
      </p>

      <ColumnChart
        buckets={HOUR_BUCKETS}
        series={series}
        height={160}
        formatX={axisLabel}
        formatTooltipX={tooltipLabel}
        xTicks={X_TICKS}
        tooltipRows={tooltipRows}
        ariaLabel="Positive outcomes by hour of day, India time"
        className="mt-2"
      />

      <div className="mt-4">
        <WeekdayHourHeatmap positive={data.grid?.positive} connected={data.grid?.connected} />
      </div>
    </div>
  );
}

export default OutcomeHours;

// ---------------------------------------------------------------------------------------------
// Weekday × hour heatmap (port of WeekdayHourHeatmap.jsx)
// ---------------------------------------------------------------------------------------------

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const HOURS = Array.from({ length: HOURS_IN_DAY }, (_, h) => h);
const GRID_COLS = '2.25rem repeat(24, minmax(0, 1fr))';
const RAMP_MIX = [20, 40, 60, 80, 100] as const;
const VIEWPORT_GAP = 8;
const TIP_GAP = 6;

/** Ramp step 1..5 → a token-only fill; 0 is the plain sunken surface (a class, not a style). */
function rampStyle(step: number): CSSProperties | undefined {
  const mix = RAMP_MIX[step - 1];
  return mix === undefined
    ? undefined
    : { background: `color-mix(in oklab, var(--success) ${mix}%, var(--surface-sunken))` };
}

const cellOf = (m: readonly (readonly number[])[] | null | undefined, d: number, h: number): number =>
  num(m?.[d]?.[h]);

function readout(d: number, h: number, pos: number, conn: number): string {
  const rate = conn > 0 ? `${Math.round((pos / conn) * 100)}%` : '—';
  return `${DAYS[d] ?? ''} ${pad2(h)}:00 · ${fmtNum(pos)} positive · ${fmtNum(conn)} answered · ${rate}`;
}

interface HeatmapProps {
  positive: readonly (readonly number[])[] | null | undefined;
  connected: readonly (readonly number[])[] | null | undefined;
}

interface ActiveCell {
  d: number;
  h: number;
  el: HTMLElement;
}

function WeekdayHourHeatmap({ positive, connected }: HeatmapProps) {
  const cellRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [focusIdx, setFocusIdx] = useState(0); // roving tab stop
  const [active, setActive] = useState<ActiveCell | null>(null);
  const [tipPos, setTipPos] = useState<{ top: number; left: number } | null>(null);

  const { max, totalPositive } = useMemo(() => {
    let m = 0;
    let t = 0;
    for (let d = 0; d < DAYS.length; d += 1) {
      for (let h = 0; h < HOURS_IN_DAY; h += 1) {
        const v = cellOf(positive, d, h);
        m = Math.max(m, v);
        t += v;
      }
    }
    return { max: m, totalPositive: t };
  }, [positive]);

  const close = useCallback(() => {
    setActive(null);
    setTipPos(null);
  }, []);

  // Centre the readout above the cell; flip below when there is no room; clamp inside the viewport.
  useLayoutEffect(() => {
    const tip = tipRef.current;
    if (!active || !tip) return;
    const a = active.el.getBoundingClientRect();
    const w = tip.offsetWidth;
    const hgt = tip.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = a.top - TIP_GAP - hgt;
    if (top < VIEWPORT_GAP) top = Math.min(a.bottom + TIP_GAP, vh - VIEWPORT_GAP - hgt);
    const left = Math.min(Math.max(VIEWPORT_GAP, a.left + a.width / 2 - w / 2), Math.max(VIEWPORT_GAP, vw - VIEWPORT_GAP - w));
    setTipPos({ top, left });
  }, [active]);

  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [active, close]);

  const show = (d: number, h: number, el: HTMLElement) => {
    setTipPos(null);
    setActive({ d, h, el });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>, d: number, h: number) => {
    let nd = d;
    let nh = h;
    if (e.key === 'ArrowRight') nh = Math.min(HOURS_IN_DAY - 1, h + 1);
    else if (e.key === 'ArrowLeft') nh = Math.max(0, h - 1);
    else if (e.key === 'ArrowDown') nd = Math.min(DAYS.length - 1, d + 1);
    else if (e.key === 'ArrowUp') nd = Math.max(0, d - 1);
    else if (e.key === 'Home') nh = 0;
    else if (e.key === 'End') nh = HOURS_IN_DAY - 1;
    else return;
    e.preventDefault();
    const idx = nd * HOURS_IN_DAY + nh;
    setFocusIdx(idx);
    cellRefs.current[idx]?.focus({ preventScroll: true });
  };

  return (
    <div className="w-full min-w-0">
      <div className="overflow-x-auto">
        <div
          role="grid"
          aria-label={`Positive outcomes by weekday and hour, ${fmtNum(totalPositive)} in total`}
          className="min-w-[560px] select-none"
          onMouseLeave={close}
        >
          <div role="row" className="mb-1 grid gap-[2px]" style={{ gridTemplateColumns: GRID_COLS }}>
            <div role="columnheader" aria-hidden="true" />
            {HOURS.map((h) => (
              <div
                key={h}
                role="columnheader"
                aria-label={hourLabel(h)}
                className="text-left text-xs leading-4 tabular-nums text-fg-subtle"
              >
                {h % 3 === 0 ? pad2(h) : ''}
              </div>
            ))}
          </div>
          <div className="flex flex-col gap-[2px]">
            {DAYS.map((day, d) => (
              <div key={day} role="row" className="grid gap-[2px]" style={{ gridTemplateColumns: GRID_COLS }}>
                <div role="rowheader" className="flex items-center text-xs text-fg-muted">
                  {day}
                </div>
                {HOURS.map((h) => {
                  const idx = d * HOURS_IN_DAY + h;
                  const pos = cellOf(positive, d, h);
                  const conn = cellOf(connected, d, h);
                  const step = heatStep(pos, max);
                  const isActive = active?.d === d && active.h === h;
                  return (
                    <div
                      key={h}
                      ref={(el) => {
                        cellRefs.current[idx] = el;
                      }}
                      role="gridcell"
                      tabIndex={idx === focusIdx ? 0 : -1}
                      aria-label={readout(d, h, pos, conn)}
                      onMouseEnter={(e) => show(d, h, e.currentTarget)}
                      onFocus={(e) => {
                        setFocusIdx(idx);
                        show(d, h, e.currentTarget);
                      }}
                      onBlur={close}
                      onKeyDown={(e) => onKeyDown(e, d, h)}
                      className={cx('h-6 rounded-sm', step === 0 && 'bg-surface-sunken', isActive && 'ring-1 ring-success')}
                      style={rampStyle(step)}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-2 flex justify-end">
        <span className="flex items-center gap-1.5 text-xs text-fg-subtle" aria-hidden="true">
          <span>Fewer</span>
          {RAMP_MIX.map((mix, i) => (
            <span key={mix} className="inline-block h-3 w-3 rounded-sm" style={rampStyle(i + 1)} />
          ))}
          <span>More</span>
        </span>
      </div>

      {active &&
        createPortal(
          <div
            ref={tipRef}
            role="tooltip"
            className="pointer-events-none fixed z-[60] whitespace-nowrap rounded-md border border-border bg-surface px-2.5 py-1.5 text-xs tabular-nums text-fg shadow-sm"
            style={tipPos ? { top: tipPos.top, left: tipPos.left } : { top: 0, left: 0, visibility: 'hidden' }}
          >
            {readout(active.d, active.h, cellOf(positive, active.d, active.h), cellOf(connected, active.d, active.h))}
          </div>,
          document.body,
        )}
    </div>
  );
}
